import { breakpointNumbers } from '@ui/constants/design-tokens/breakpoints';
import { Order } from '@ui/hooks/trade/useTradeOrders/types';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import BigNumber from 'bignumber.js';
import { type TFunction } from 'next-i18next';
import { token } from 'styled-system/tokens';
import type {
  IChartWidgetApi,
  IOrderLineAdapter,
  IPositionLineAdapter,
} from 'trading-view';

const applyLineOptions = ({
  line,
  options,
}: {
  options: {
    backgroundColor?: string;
    color?: string;
    price: number;
    text: string;
  };
  line: IPositionLineAdapter | IOrderLineAdapter;
}) => {
  const _style = {
    backgroundColor: token('colors.greyWhite.lightGrey4'),
    color: token('colors.greyWhite.white'),
    ...options,
  };
  if (window.document.body.clientWidth < breakpointNumbers.tablet) {
    line.setLineLength(
      -16, // 16 pixel offset from left border
      'pixel',
    );
  }

  return line
    .setPrice(options.price)
    .setText(options.text)

    .setQuantityBorderColor(_style.backgroundColor)
    .setQuantityBackgroundColor(_style.backgroundColor)
    .setQuantity('') // quantity not applicable in our case yet, set '' to avoid occupying space

    .setLineColor(_style.backgroundColor)
    .setLineWidth(2)
    .setLineStyle(1)

    .setBodyTextColor(_style.color)
    .setBodyBorderColor(_style.backgroundColor)
    .setBodyBackgroundColor(_style.backgroundColor);
};

export const drawOpenOrderLine = ({
  t,
  order,
  chartApi,
}: {
  order: Pick<Order, 'isLong' | 'triggerPrice'>;
  t: TFunction;
  chartApi: IChartWidgetApi;
}) => {
  const longOrShortText = order.isLong ? t(`common.Long`) : t(`common.Short`);

  const title = `Limit ${longOrShortText}`;

  return applyLineOptions({
    line: chartApi.createOrderLine(),
    options: {
      price: order.triggerPrice.toNumber(),
      text: title,
    },
  });
};
export const drawTakeProfitLine = ({
  takeProfit,
  chartApi,
}: {
  takeProfit: {
    triggerPrice: BigNumber;
    isLong: boolean;
  };
  chartApi: IChartWidgetApi;
}) => {
  return applyLineOptions({
    line: chartApi.createOrderLine(),
    options: {
      backgroundColor: token('colors.system.success'),
      // color: token('colors.black.fullBlack'),
      price: takeProfit.triggerPrice.toNumber(),
      text: `TP ${takeProfit.isLong ? 'Long' : 'Short'}`,
    },
  });
};

export const drawStopLossLine = ({
  stopLoss,
  chartApi,
}: {
  stopLoss: {
    triggerPrice: BigNumber;
    isLong: boolean;
  };
  chartApi: IChartWidgetApi;
}) => {
  return applyLineOptions({
    line: chartApi.createOrderLine(),
    options: {
      backgroundColor: token('colors.system.errorDark'),
      color: token('colors.greyWhite.white'),
      price: stopLoss.triggerPrice.toNumber(),
      text: `SL ${stopLoss.isLong ? 'Long' : 'Short'}`,
    },
  });
};

export const drawPositionLine = ({
  t,
  position,
  chartApi,
}: {
  position: Pick<Position, 'isLong' | 'entryPrice'>;
  t: TFunction;
  chartApi: IChartWidgetApi;
}) => {
  const longOrShortText = position.isLong
    ? t(`common.Long`)
    : t(`common.Short`);

  const positionLine = applyLineOptions({
    line: chartApi.createPositionLine(),
    options: {
      price: position.entryPrice.toNumber(),
      text: `Entry ${longOrShortText}`,
      color: token('colors.black.fullBlack'),
      backgroundColor: '#fff',
    },
  });

  return positionLine;
};

export const drawLiqPriceLine = ({
  chartApi,
  isLong,
  liqPrice,
  t,
}: {
  t: TFunction;
  isLong: boolean;
  liqPrice: BigNumber;
  chartApi: IChartWidgetApi;
}) => {
  const longOrShortText = isLong ? t(`common.Long`) : t(`common.Short`);
  const liquidationLine = applyLineOptions({
    line: chartApi.createPositionLine(),
    options: {
      text: `Liq ${longOrShortText}`,
      price: liqPrice.toNumber(),
      backgroundColor: token('colors.system.errorLight'),
      color: token('colors.black.fullBlack'),
    },
  });

  return liquidationLine;
};
